import * as R from 'ramda';
import { GknServiceData } from './GatekeeperNetworkServiceData';
import { getNetworkNameByChainId } from '../../utils/utils';
import { networkConfigs } from '../../common/types';

/**
 * If the given GKN settings have 'constraints.chainConstraintOverrides' set,
 * deep merge them into the top-level settings based on the given chain type and network.
 * @param rawSettings
 * @param chain
 */
const mergeChainSpecificConstraints =
  (chain?: string, chainNetwork?: string) =>
  (rawSettings: GknServiceData): GknServiceData => {
    if (!chain || !chainNetwork) {
      return rawSettings;
    }

    const settingsForChainType = rawSettings?.constraints?.chainConstraintOverrides?.[chain]?.default || {};

    // get constraints for the specific chain network (by chainId or chain name)
    const chainNetworkName = getNetworkNameByChainId(chainNetwork, networkConfigs);
    const settingsForChainNetwork =
      rawSettings?.constraints?.chainConstraintOverrides?.[chain]?.[chainNetworkName || ''] ||
      rawSettings?.constraints?.chainConstraintOverrides?.[chain]?.[chainNetwork || ''] ||
      {};

    const mergedSettings = {
      ...rawSettings,
      constraints: R.mergeDeepRight(
        rawSettings.constraints,
        R.mergeDeepRight(settingsForChainType, settingsForChainNetwork),
      ),
    };
    return R.dissocPath(['constraints', 'chainConstraintOverrides'], mergedSettings);
  };

/**
 * Merge the network-specific settings into the chain top-level settings,
 * based on the given chain type and network.
 * @param chain
 * @param chainNetwork
 */
const mergeNetworkSpecificChainSettings =
  (chain?: string, chainNetwork?: string) =>
  (rawSettings: GknServiceData): GknServiceData => {
    if (!chain || !chainNetwork) {
      return rawSettings;
    }

    const settingsForChainType = rawSettings?.chains?.[chain] || {};

    // get constraints for the specific chain network (by chainId or chain name)
    const chainNetworkName = getNetworkNameByChainId(chainNetwork, networkConfigs);
    const settingsForChainNetwork =
      rawSettings?.chains?.[chain]?.networks?.[chainNetworkName || ''] ||
      rawSettings?.chains?.[chain]?.networks?.[chainNetwork || ''] ||
      {};

    return {
      ...rawSettings,
      chains: {
        ...rawSettings.chains,
        [chain]: {
          // remove as the network-specific settings are already merged
          ...R.omit(['networks'], settingsForChainType),
          ...settingsForChainNetwork,
        },
      },
    };
  };

export const mergeAllChainSpecificSettings = (
  rawSettings: GknServiceData,
  chain?: string,
  chainNetwork?: string,
): GknServiceData =>
  R.pipe(
    mergeChainSpecificConstraints(chain, chainNetwork),
    mergeNetworkSpecificChainSettings(chain, chainNetwork),
  )(rawSettings);

// Allowed keys for arbitrary value retrieval from the GKN data
type GknValueKeys =
  | 'requiredDeclarations'
  | 'declaration'
  | 'termsAndConditions'
  | 'expiry'
  | 'tokenActiveDisplay'
  | 'uniquenessBurnCooldownMinutes'
  | 'testnetUniquenessBurnCooldownMinutes'
  | 'scopeRequestRequiredTemplateId'
  | 'credentialPrePassCreation'
  | 'credentialPostPassCreation'
  | 'credentialStorageEnabled'
  | 'scopeRequestTimeout';
// Allowed keys for localised string retrieval from the GKN data
type GknLocalisedValueKeys = 'acceptedDeclaration';

// Mapping of keys to their paths in the GKN data
const gknKeyPathMapping: Record<GknValueKeys | GknLocalisedValueKeys, string[]> = {
  requiredDeclarations: ['constraints', 'public', 'declarations', 'requiredDeclarations'],
  declaration: ['constraints', 'public', 'declarations', 'requiredDeclarations', 'acceptedDeclaration'],
  termsAndConditions: [
    'constraints',
    'public',
    'declarations',
    'requiredDeclarations',
    'acceptedTermsAndConditionsLink',
  ],
  acceptedDeclaration: ['constraints', 'public', 'declarations', 'requiredDeclarations', 'acceptedDeclaration'],
  expiry: ['constraints', 'public', 'expiry'],
  tokenActiveDisplay: ['client', 'tokenActiveDisplay'],
  uniquenessBurnCooldownMinutes: [
    'constraints',
    'public',
    'proofOfPersonhood',
    'faceLivenessCheck',
    'uniquenessBurnCooldownMinutes',
  ],
  testnetUniquenessBurnCooldownMinutes: [
    'constraints',
    'public',
    'proofOfPersonhood',
    'faceLivenessCheck',
    'testnetUniquenessBurnCooldownMinutes',
  ],
  scopeRequestRequiredTemplateId: ['constraints', 'public', 'kyc', 'scopeRequest', 'requiredTemplateId'],
  scopeRequestTimeout: ['constraints', 'public', 'kyc', 'scopeRequest', 'timeout'],
  credentialPrePassCreation: ['client', 'credentialStorageEnabled', 'prePassCreation'],
  credentialPostPassCreation: ['client', 'credentialStorageEnabled', 'postPassCreation'],
  credentialStorageEnabled: ['client', 'credentialStorageEnabled'],
};

/**
 * Retrieve specific values from the GKN data, especially those nested deep within the constraints.
 *
 * */
export const getGknValue = <T>(gknData: GknServiceData, key: GknValueKeys): T => {
  const path = gknKeyPathMapping[key];
  return R.path(path, gknData) as T;
};

/**
 * Retrieve specific localised values from the GKN data, especially those nested deep within the constraints.
 * This assumes the value is a string or an object with keys as locales and values as stringsx
 * If the locale is not found, it defaults to 'en'.
 * */
export const getGknLocalisedString = (gknData: GknServiceData, key: GknLocalisedValueKeys): string | undefined => {
  const path = gknKeyPathMapping[key];
  const result = R.path(path, gknData);
  if (R.isNil(result) || R.isEmpty(result)) {
    return undefined;
  }
  // If we have an array, take the first element
  if (Array.isArray(result)) {
    return (result as Array<string>)[0] || '';
  }
  if (typeof result === 'object') {
    // If it's an object, lookup by locale that matches the browser language
    // Use only the first part of the browser language, so en-US becomes en
    const browserLanguage = navigator.language.split('-')[0];
    return (result as Record<string, string>)[browserLanguage] || (result as Record<string, string>).en || '';
  }
  return result as string;
};

export default mergeAllChainSpecificSettings;
